import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, Alert, Modal, Table } from "react-bootstrap";
import { treatmentTypes, treatmentStatuses, toothConditions } from "../dentist/data/teethData";
import { showToast } from "../../reusables/CommonFunction";
import { formatTo2Decimals } from '../dentist/Formatter';

const TreatmentPlanForm = ({ show, toothNumber, initialCondition, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        condition: initialCondition || "healthy",
        treatment_type: "",
        treatment_date: "",
        cost: "",
        treatment_status: "planned",
        note: "",
        discount_percentage: "",
        discount_amount: "",
        payment_type: "full",
        initial_payment: "",
        payment_method: "cash",
    });

    const [calculatedAmounts, setCalculatedAmounts] = useState({
        originalCost: 0,
        discountAmount: 0,
        finalCost: 0,
        paidAmount: 0,
        remainingBalance: 0
    });

    useEffect(() => {
        if (initialCondition) {
            setFormData(prev => ({ ...prev, condition: initialCondition }));
        }
    }, [initialCondition]);

    useEffect(() => {
        if (formData.cost) {
            calculateAmounts(formData);
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updated = { ...formData, [name]: value };
        setFormData(updated);

        if (['cost', 'discount_percentage', 'discount_amount', 'initial_payment', 'payment_type'].includes(name)) {
            calculateAmounts(updated);
        }
    };

    const calculateAmounts = (data) => {
        const originalCost = formatTo2Decimals(data.cost || 0);

        let discountAmount = 0;
        if (data.discount_percentage) {
            discountAmount = formatTo2Decimals((originalCost * parseFloat(data.discount_percentage)) / 100);
        } else if (data.discount_amount) {
            discountAmount = formatTo2Decimals(data.discount_amount);
        }

        const finalCost = formatTo2Decimals(originalCost - discountAmount);

        let initialPayment = 0;
        if (data.payment_type === "full") {
            initialPayment = finalCost;
        } else if (data.payment_type === "installment") {
            initialPayment = formatTo2Decimals(parseFloat(data.initial_payment) || 0);
            if (initialPayment > finalCost) initialPayment = finalCost; // guard against overpay
        }

        const remainingBalance = formatTo2Decimals(finalCost - initialPayment);

        setCalculatedAmounts({
            originalCost,
            discountAmount,
            finalCost,
            paidAmount: initialPayment,
            remainingBalance: Math.max(0, remainingBalance)
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.treatment_type) {
            showToast("error", "Please select a treatment type.");
            return;
        }
        if (!formData.treatment_date) {
            showToast("error", "Please select a treatment date.");
            return;
        }
        if (!formData.cost || parseFloat(formData.cost) <= 0) {
            showToast("error", "Please enter a valid cost greater than 0.");
            return;
        }

        // Validate initial payment for installment
        if (formData.payment_type === 'installment') {
            const maxAllowed = calculatedAmounts.finalCost;
            const entered = parseFloat(formData.initial_payment) || 0;
            if (entered > maxAllowed) {
                showToast("error", `Initial payment cannot be more than ₹${maxAllowed.toFixed(2)}.`);
                return;
            }
        }

        const finalData = {
            condition: formData.condition,
            treatment_type: formData.treatment_type,
            treatment_date: formData.treatment_date,
            treatment_status: formData.treatment_status,
            note: formData.note,
            discount_percentage: formData.discount_percentage,
            original_cost: calculatedAmounts.originalCost,
            discount_amount: calculatedAmounts.discountAmount,
            final_cost: calculatedAmounts.finalCost,
            paid_amount: calculatedAmounts.paidAmount,
            remaining_balance: calculatedAmounts.remainingBalance,
            payment_type: formData.payment_type,
            dental_payment_history: formData.payment_type === 'full'
                ? [{ amount: calculatedAmounts.finalCost, date: formData.treatment_date, amount_paid_via: formData.payment_method }]
                : calculatedAmounts.paidAmount > 0
                    ? [{ amount: calculatedAmounts.paidAmount, date: formData.treatment_date, amount_paid_via: formData.payment_method }]
                    : []
        };

        onSave(finalData);
    };

    return (
        <Modal show={show} onHide={onCancel} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Treatment Plan - Tooth #{toothNumber}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    {/* Condition */}
                    <Form.Group controlId="condition" className="mb-3">
                        <Form.Label>Tooth Condition</Form.Label>
                        <Form.Select name="condition" value={formData.condition} onChange={handleInputChange}>
                            {toothConditions.map(cond => (
                                <option key={cond.value} value={cond.value}>{cond.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Type & Date */}
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="treatmentType">
                                <Form.Label className="required">Treatment Type</Form.Label>
                                <Form.Select name="treatment_type" value={formData.treatment_type} onChange={handleInputChange}>
                                    <option value="">Select Treatment</option>
                                    {treatmentTypes.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="treatmentDate">
                                <Form.Label className="required">Treatment Date</Form.Label>
                                <Form.Control type="date" name="treatment_date" value={formData.treatment_date} onChange={handleInputChange} />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Cost & Status */}
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="cost">
                                <Form.Label className="required">Treatment Cost</Form.Label>
                                <Form.Control type="number" name="cost" value={formData.cost} onChange={handleInputChange} placeholder="0.00" onWheel={(e) => e.target.blur()} />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="status">
                                <Form.Label>Treatment Status</Form.Label>
                                <Form.Select name="treatment_status" value={formData.treatment_status} onChange={handleInputChange}>
                                    {treatmentStatuses.map((status) => (
                                        <option key={status.value} value={status.value}>{status.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Discounts */}
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="discountPercentage">
                                <Form.Label>Discount (%)</Form.Label>
                                <Form.Control type="number" name="discount_percentage" value={formData.discount_percentage} onChange={handleInputChange} min="0" max="100" disabled={formData.discount_amount} onWheel={(e) => e.target.blur()} />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="discountAmount">
                                <Form.Label>Discount Amount (₹)</Form.Label>
                                <Form.Control type="number" name="discount_amount" value={formData.discount_amount} onChange={handleInputChange} placeholder="0.00" disabled={formData.discount_percentage} onWheel={(e) => e.target.blur()} />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Payment type */}
                    <Form.Group controlId="paymentType" className="mb-3">
                        <Form.Label>Payment Type</Form.Label>
                        <Form.Select name="payment_type" value={formData.payment_type} onChange={handleInputChange}>
                            <option value="full">Pay in Full</option>
                            <option value="installment">Pay in Installments</option>
                        </Form.Select>
                    </Form.Group>

                    {formData.payment_type === 'installment' && (
                        <Row>
                            <Col md={6} className="mb-3">
                                <Form.Group controlId="initialPayment">
                                    <Form.Label>Initial Payment (₹)</Form.Label>
                                    <Form.Control type="number" name="initial_payment" value={formData.initial_payment} onChange={handleInputChange} max={calculatedAmounts.finalCost} onWheel={(e) => e.target.blur()} />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3 d-flex align-items-end">
                                <small className="text-muted">Remaining: <strong>₹{calculatedAmounts.remainingBalance.toFixed(2)}</strong></small>
                            </Col>
                        </Row>
                    )}

                    {/* Summary */}
                    {formData.cost && (
                        <Alert variant="info" className="mb-3">
                            <h6 className="mb-3">Payment Summary (Tooth #{toothNumber})</h6>
                            <Table bordered size="sm" responsive className="mb-0">
                                <tbody>
                                    <tr>
                                        <td>Original Cost</td>
                                        <td className="text-end">₹{calculatedAmounts.originalCost.toFixed(2)}</td>
                                    </tr>
                                    {(formData.discount_percentage || formData.discount_amount) && (
                                        <tr>
                                            <td>
                                                Discount{" "} {formData.discount_percentage ? `(${formData.discount_percentage}%)` : "(Flat)"}
                                            </td>
                                            <td className="text-end">-₹{calculatedAmounts.discountAmount.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td><strong>Final Cost</strong></td>
                                        <td className="text-end"><strong>₹{calculatedAmounts.finalCost.toFixed(2)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Paid</td>
                                        <td className="text-end text-success">₹{calculatedAmounts.paidAmount.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Remaining</td>
                                        <td className="text-end text-warning">₹{calculatedAmounts.remainingBalance.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Alert>
                    )}

                    {/* Payment method */}
                    <Form.Group controlId="paymentMethod" className="mb-3">
                        <Form.Label>Payment Method</Form.Label>
                        <Form.Select name="payment_method" value={formData.payment_method} onChange={handleInputChange}>
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="upi">UPI</option>
                            <option value="other">Other</option>
                        </Form.Select>
                    </Form.Group>

                    {/* Notes */}
                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control as="textarea" name="note" value={formData.note} onChange={handleInputChange} rows={3} />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                <Button variant="success" type="submit" onClick={handleSubmit}>Save Treatment Plan</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TreatmentPlanForm;
